import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { useQuery } from '@tanstack/react-query'

import { formatDateToString } from '~/helpers'
import useQueryParams from '~/hooks/useQuery'
import Chart from '~/modules/retailMedia/components/Chart'
import WrapperChartAndCards from '~/modules/retailMedia/components/WrapperChartAndCards'
import { parseLookbackMonths } from '~/modules/retailMedia/constants'
import { formatIndustryReportHistoryCampaign } from '~/modules/retailMedia/dtos/industry/campaign/reportHistory'
import { buildRoasAttributionChartMarker } from '~/modules/retailMedia/helpers/roasAttributionChartMarker'
import { useNtbMetricsEnabled } from '~/modules/retailMedia/hooks/useNtbMetricsEnabled'
import {
  getCampaignHistoryReportService,
  getCampaignsReportHistoryKey
} from '~/modules/retailMedia/services/report'
import { useAppSelector } from '~/store/hooks'

import CardsCampaign from '../Cards'

const MetricsCampaignView = ({
  adType,
  campaignAdType,
  strategy,
  target,
  selectQuantity = 3,
  initialMetrics = ['clicks', 'impressions', 'conversions']
}: MetricsProps = {}) => {
  const { id } = useParams()
  const { t } = useTranslation()

  const { startDate, endDate, compareStartDate, compareEndDate } =
    useAppSelector(state => state.datepickerHeader)
  const hasCompareInterval = !!compareStartDate && !!compareEndDate

  const ntbMetricsEnabled = useNtbMetricsEnabled()
  // grão de campanha: sempre um campaign_id só, sem ambiguidade de escopo
  const excludeChartMetrics = useMemo(
    () =>
      ntbMetricsEnabled
        ? []
        : (['newToBrandCustomers', 'newToBrandRevenue'] as AvailablePresets[]),
    [ntbMetricsEnabled]
  )

  const searchParams = useQueryParams()
  const product_sku = searchParams.get('product_sku') as RetailMediaAdsStatuses
  const ad_status = searchParams.get('ad_status') as RetailMediaAdsStatuses
  // mesma janela que os cards desta tela leem da URL
  const lookbackMonths = parseLookbackMonths(
    searchParams.get('lookback_months')
  )

  const queryParams = useMemo(
    () => ({
      start_date: formatDateToString(startDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(endDate, 'YYYY-MM-DD'),
      campaign_id: id,
      product_sku: adType === 'product' ? product_sku : undefined,
      ad_status,
      ad_type: adType,
      target,
      lookback_months: lookbackMonths
    }),
    [
      startDate,
      endDate,
      id,
      adType,
      product_sku,
      ad_status,
      target,
      lookbackMonths
    ]
  )

  const queryParamsCompare = useMemo(
    () => ({
      start_date: formatDateToString(compareStartDate, 'YYYY-MM-DD'),
      end_date: formatDateToString(compareEndDate, 'YYYY-MM-DD'),
      campaign_id: id,
      product_sku: adType === 'product' ? product_sku : undefined,
      ad_status,
      ad_type: adType,
      target,
      lookback_months: lookbackMonths
    }),
    [
      compareStartDate,
      compareEndDate,
      id,
      adType,
      product_sku,
      ad_status,
      target,
      lookbackMonths
    ]
  )

  const {
    data: chartData,
    isLoading: chartLoading,
    isPending,
    error: chartError
  } = useQuery({
    queryKey: [getCampaignsReportHistoryKey, id, ...Object.values(queryParams)],
    queryFn: async () => {
      const { data } = await getCampaignHistoryReportService(queryParams)

      return formatIndustryReportHistoryCampaign(data, adType)
    }
  })

  const { data: chartDataCompare } = useQuery({
    queryKey: [
      getCampaignsReportHistoryKey,
      'compare',
      id,
      ...Object.values(queryParamsCompare)
    ],
    queryFn: async () => {
      const { data } = await getCampaignHistoryReportService(queryParamsCompare)

      return formatIndustryReportHistoryCampaign(data, adType)
    },
    enabled: hasCompareInterval
  })

  const chartMarker = useMemo(() => {
    if (strategy === 'objective_based') return undefined

    return buildRoasAttributionChartMarker({
      data: chartData,
      compareData: chartDataCompare,
      isCompare: hasCompareInterval,
      t
    })
  }, [strategy, chartData, chartDataCompare, hasCompareInterval, t])

  return (
    <WrapperChartAndCards>
      <Chart
        isValidContent={!isPending}
        chartData={chartData}
        chartDataCompare={chartDataCompare}
        isCompare={hasCompareInterval}
        marker={chartMarker}
        compareInterval={{ start: compareStartDate, end: compareEndDate }}
        loading={chartLoading}
        error={chartError}
        initialMetrics={initialMetrics}
        additionalMetricsKeys={['totalSpent', 'income']}
        selectQuantity={selectQuantity}
        adType={adType}
        excludeMetrics={excludeChartMetrics}
      />

      <CardsCampaign
        strategy={strategy}
        adType={adType}
        campaignAdType={campaignAdType}
        target={target}
      />
    </WrapperChartAndCards>
  )
}

export default MetricsCampaignView
